/**
 * Languages inside one pe3d variant, as the editor sees them.
 *
 * "en" is the variant itself. Every other language is a sparse override in
 * `variant.localizationOverrides[lang]` ({ config, assets, storeLinks,
 * endType, sipPlayable }) and is listed in `variant.languages` — the engine's
 * format (PlayEngine3D config/localization.ts), resolved by the release's own
 * `resolveVariant`. Only releases whose bundle lists the "localization"
 * feature have it; for any other release there is only "en" and the variant
 * is edited exactly as before.
 *
 * The panels edit a language's VIEW (the variant as that language resolves)
 * and never the override itself: `writeView` turns an edited view back into
 * the override — whatever differs from the base — so a field set back to the
 * base's value stops being overridden.
 */

import _ from "lodash";

export const BASE_LANGUAGE = "en";

/**
 * The languages offered to add, with their captions: the map every playEngine base carries as
 * `supportedLanguages` (server controller/base/playEngineBaseShape.js); a pe3d base has none of
 * its own. Codes a variant has that are not in it are listed too.
 */
export const LANGUAGE_CAPTIONS = Object.freeze({
  "en": "English - En", "af": "Afrikaans - Af", "sq": "Albanian - Sq", "ar": "Arabic - Ar",
  "hy": "Armenian - Hy", "az": "Azerbaijani - Az", "be": "Belarusian - Be", "bg": "Bulgarian - Bg",
  "ca": "Catalan - Ca", "zh": "Chinese - Zh", "hr": "Croatian - Hr", "cs": "Czech - Cs",
  "da": "Danish - Da", "nl": "Dutch - Nl", "eo": "Esperanto - Eo", "et": "Estonian - Et",
  "fi": "Finnish - Fi", "fr": "French - Fr", "de": "German - De", "el": "Greek - El",
  "he": "Hebrew - He", "hi": "Hindi - Hi", "hu": "Hungarian - Hu", "is": "Icelandic - Is",
  "id": "Indonesian - Id", "it": "Italian - It", "ja": "Japanese - Ja", "kk": "Kazakh - Kk",
  "ko": "Korean - Ko", "lb": "Luxembourgish - Lb", "lt": "Lithuanian - Lt", "lv": "Latvian - Lv",
  "mk": "Macedonian - Mk", "ms": "Malay - Ms", "no": "Norwegian - No", "pl": "Polish - Pl",
  "pt": "Portuguese - Pt", "pt-br": "Portuguese - Pt-Br", "ro": "Romanian - Ro", "ru": "Russian - Ru",
  "sr": "Serbian - Sr", "sk": "Slovak - Sk", "sl": "Slovenian - Sl", "es": "Spanish - Es",
  "sv": "Swedish - Sv", "th": "Thai - Th", "tl": "Tagalog - Tl", "tr": "Turkish - Tr",
  "uk": "Ukrainian - Uk", "vi": "Vietnamese - Vi",
});
const PLAYABLE_KEYS = ["endType", "sipPlayable"];

export const supportsLocalization = (release) => Boolean(release && (release.bundle.features || []).includes("localization") && release.code.resolveVariant);

/** The languages a variant offers, "en" first. */
export function variantLanguages(variant) {
  const out = [BASE_LANGUAGE];
  for (const language of [...((variant && variant.languages) || []), ...Object.keys((variant && variant.localizationOverrides) || {})])
    if (!out.includes(language)) out.push(language);
  return out;
}

/** The base variant: everything but the language fields. */
const baseOf = (variant) => _.omit(variant || {}, ["languages", "localizationOverrides"]);

/** The variant as `language` sees it, without language fields; the draft itself where there are no languages. */
export function viewOf(release, variant, language) {
  if (!variant) return variant;
  if (!supportsLocalization(release)) return variant;
  if (language === BASE_LANGUAGE) return baseOf(variant);
  return release.code.resolveVariant(variant, language);
}

/** The config leaves that differ from the base (groups recurse, everything else compared whole, like the engine's merge). */
function configDiff(fields, base, next) {
  const out = {};
  for (const [key, field] of Object.entries(fields)) {
    if (field.kind === "group") {
      const sub = configDiff(field.fields, (base && base[key]) || {}, (next && next[key]) || {});
      if (!_.isEmpty(sub)) out[key] = sub;
    } else if (next && next[key] !== undefined && !_.isEqual(next[key], base && base[key])) out[key] = _.cloneDeep(next[key]);
  }
  return out;
}

/** What `language`'s view differs from the base by: its override. Empty parts are dropped. */
function overrideOf(release, base, view) {
  const fields = release.bundle.schema.config;
  const override = {};
  const config = configDiff(fields, release.code.effectiveConfig(fields, base.config), release.code.effectiveConfig(fields, view.config));
  if (!_.isEmpty(config)) override.config = config;
  const assets = _.pickBy(view.assets || {}, (ref, slot) => ref && !_.isEqual(ref, (base.assets || {})[slot]));
  if (!_.isEmpty(assets)) override.assets = assets;
  const links = _.pickBy(view.storeLinks || {}, (link, platform) => link !== ((base.storeLinks || {})[platform] || ""));
  if (!_.isEmpty(links)) override.storeLinks = links;
  for (const key of PLAYABLE_KEYS) if (view[key] !== undefined && view[key] !== base[key]) override[key] = view[key];
  return override;
}

/**
 * The variant after `update` edited `language`'s view. "en" (or a release
 * without languages) edits the variant itself; another language becomes the
 * override that view needs.
 */
export function writeView(release, variant, language, update) {
  if (!supportsLocalization(release) || language === BASE_LANGUAGE) {
    if (!supportsLocalization(release)) return update(_.cloneDeep(variant || {}));
    const languageFields = _.pick(variant || {}, ["languages", "localizationOverrides"]);
    return { ...update(baseOf(_.cloneDeep(variant))), ...languageFields };
  }
  const next = _.cloneDeep(variant || {});
  const view = update(viewOf(release, next, language));
  const override = overrideOf(release, baseOf(next), view);
  next.localizationOverrides = { ...(next.localizationOverrides || {}), [language]: override };
  next.languages = _.uniq([...(next.languages || []), language]);
  return next;
}

/** The override of one language ({} for "en"): what the panels mark as "differs from en". */
export const overrideFor = (variant, language) => (language === BASE_LANGUAGE ? {} : _.get(variant, ["localizationOverrides", language], {}));

/** Whether a config path ("text.cta"), slot or playable key is overridden in `language`. */
export function isOverridden(variant, language, kind, key) {
  const override = overrideFor(variant, language);
  if (kind === "config") return _.has(override.config || {}, key);
  if (kind === "assets") return _.has(override.assets || {}, key);
  if (kind === "storeLinks") return _.has(override.storeLinks || {}, key);
  return override[key] !== undefined;
}

/** The variant with one overridden value of `language` back on the base's. */
export function resetOverride(variant, language, kind, key) {
  const next = _.cloneDeep(variant);
  const override = _.get(next, ["localizationOverrides", language]);
  if (!override) return next;
  if (kind === "config") {
    _.unset(override.config, key);
    pruneEmpty(override, "config");
  } else if (kind === "assets" || kind === "storeLinks") {
    delete (override[kind] || {})[key];
    if (_.isEmpty(override[kind])) delete override[kind];
  } else delete override[key];
  return next;
}

/** Drops groups an unset left empty. */
function pruneEmpty(owner, key) {
  const value = owner[key];
  if (!_.isPlainObject(value)) return;
  for (const child of Object.keys(value)) pruneEmpty(value, child);
  if (_.isEmpty(value)) delete owner[key];
}

export function addLanguage(variant, language) {
  const next = _.cloneDeep(variant || {});
  if (language === BASE_LANGUAGE) return next;
  next.languages = _.uniq([...(next.languages || []), language]);
  next.localizationOverrides = { ...(next.localizationOverrides || {}), [language]: (next.localizationOverrides || {})[language] || {} };
  return next;
}

export function removeLanguage(variant, language) {
  const next = _.cloneDeep(variant || {});
  next.languages = (next.languages || []).filter((code) => code !== language);
  if (next.localizationOverrides) delete next.localizationOverrides[language];
  if (!next.languages.length) delete next.languages;
  if (_.isEmpty(next.localizationOverrides)) delete next.localizationOverrides;
  return next;
}

/** Every slot reference of the variant, its languages' too: [{ slot, ref, language }] (`language` null for the base). */
export function assetRefsOf(variant) {
  const out = Object.entries((variant && variant.assets) || {}).map(([slot, ref]) => ({ slot, ref, language: null }));
  for (const [language, override] of Object.entries((variant && variant.localizationOverrides) || {}))
    for (const [slot, ref] of Object.entries((override && override.assets) || {})) out.push({ slot, ref, language });
  return out;
}
